"use client";

/**
 * DashboardTour — Spotlight product tour shown once after the WelcomeModal.
 *
 * Anchors via `data-tour="..."` attributes on sidebar items + key dashboard
 * widgets. Auto-starts when:
 *   - the user has dismissed/completed the WelcomeModal (welcome_seen = true)
 *   - the tour hasn't run yet (localStorage "velyq.tour.completed" missing)
 *
 * Persistence is intentionally client-side for now. Per-user backend state
 * would require a new column + endpoint; the trade-off is the tour replays
 * if the user clears storage, which we consider acceptable for v1.
 */
import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { TourProvider, useTour, type StepType } from "@reactour/tour";
import { useAuthStore } from "@/lib/store";
import { users as usersApi } from "@/lib/api";
import { usePathname } from "next/navigation";
const STORAGE_KEY = "velyq.tour.completed";
interface TourCopy {
  title: {
    fr: string;
    en: string;
  };
  body: {
    fr: string;
    en: string;
  };
}
const STEPS: Array<{
  selector: string;
  copy: TourCopy;
}> = [{
  selector: "[data-tour='dash-hero']",
  copy: {
    title: {
      fr: "Ton dashboard",
      en: "Your dashboard"
    },
    body: {
      fr: "C'est ton centre de contrôle. Score moyen, prochain entretien, raccourcis vers chaque outil.",
      en: "Your control center. Average score, next interview, shortcuts to every tool."
    }
  }
}, {
  selector: "[data-tour='dash-checklist']",
  copy: {
    title: {
      fr: "Premiers pas",
      en: "Getting started"
    },
    body: {
      fr: "Quatre étapes pour démarrer : compte, CV, profil, premier entretien. La checklist disparaît une fois tout coché.",
      en: "Four steps to get going: account, CV, profile, first interview. The checklist hides itself once everything's ticked."
    }
  }
}, {
  selector: "[data-tour='dash-tools']",
  copy: {
    title: {
      fr: "Outils rapides",
      en: "Quick tools"
    },
    body: {
      fr: "Accès direct aux quatre fonctionnalités principales avec ton score / progression sur chacune.",
      en: "Direct access to the four main features with your score and progression on each."
    }
  }
}, {
  selector: "[data-tour='dash-applications-table']",
  copy: {
    title: {
      fr: "Tes candidatures",
      en: "Your applications"
    },
    body: {
      fr: "Vue résumée de tes candidatures actives. Clique sur une ligne pour voir le détail dans le tracker.",
      en: "Summary view of your active applications. Click a row to drill into the tracker."
    }
  }
}, {
  selector: "[data-tour='nav-dashboard']",
  copy: {
    title: {
      fr: "Dashboard",
      en: "Dashboard"
    },
    body: {
      fr: "Reviens toujours ici pour la vue d'ensemble — score global, prochaine action, candidatures.",
      en: "Always come back here for the overview — global score, next action, applications."
    }
  }
}, {
  selector: "[data-tour='nav-resumes']",
  copy: {
    title: {
      fr: "Mes CVs",
      en: "My CVs"
    },
    body: {
      fr: "Upload tes CVs (PDF ou Word). On les parse et tu peux corriger les infos extraites.",
      en: "Upload your resumes (PDF or Word). We parse them and you can correct the extracted data."
    }
  }
}, {
  selector: "[data-tour='nav-ats']",
  copy: {
    title: {
      fr: "Analyse ATS",
      en: "ATS Analysis"
    },
    body: {
      fr: "Compare ton CV à une offre d'emploi : score de matching, mots-clés manquants, suggestions concrètes.",
      en: "Match your resume against a job offer: compatibility score, missing keywords, concrete fixes."
    }
  }
}, {
  selector: "[data-tour='nav-cover-letters']",
  copy: {
    title: {
      fr: "Lettres de motivation",
      en: "Cover letters"
    },
    body: {
      fr: "Génère des lettres personnalisées à partir de ton CV et de l'offre. Tu peux toujours éditer le résultat.",
      en: "Generate personalized letters from your CV and the offer. Always editable."
    }
  }
}, {
  selector: "[data-tour='nav-applications']",
  copy: {
    title: {
      fr: "Tracker de candidatures",
      en: "Application tracker"
    },
    body: {
      fr: "Pipeline visuel de tes candidatures (Kanban). Status, contact, dates, rappels.",
      en: "Visual pipeline of your applications (Kanban). Status, contact, dates, reminders."
    }
  }
}, {
  selector: "[data-tour='nav-linkedin']",
  copy: {
    title: {
      fr: "LinkedIn",
      en: "LinkedIn"
    },
    body: {
      fr: "Importe ton profil LinkedIn pour pré-remplir candidatures et CV. Optimise aussi ton profil pour les recruteurs.",
      en: "Import your LinkedIn profile to pre-fill applications and CV. Also optimize your profile for recruiters."
    }
  }
}, {
  selector: "[data-tour='nav-interviews']",
  copy: {
    title: {
      fr: "Entretiens simulés",
      en: "Mock interviews"
    },
    body: {
      fr: "Sessions d'entretien IA — questions ciblées sur l'offre, feedback immédiat, mode vocal live (Premium).",
      en: "AI interview sessions — questions targeting the offer, instant feedback, live voice mode (Premium)."
    }
  }
}, {
  selector: "[data-tour='nav-training']",
  copy: {
    title: {
      fr: "Training Hub",
      en: "Training Hub"
    },
    body: {
      fr: "Cas pratiques courts (5–10 min) : cold call, pitch, négociation, conversations difficiles. Scoring détaillé.",
      en: "Short scenario practice (5-10 min): cold call, pitch, negotiation, difficult conversations. Detailed scoring."
    }
  }
}];
function buildSteps(locale: "fr" | "en"): StepType[] {
  const total = STEPS.length;
  return STEPS.map((s, i) => ({
    selector: s.selector,
    // Dashboard-widget steps (dash-*): centered, modal-style. The spotlight
    // cutout still highlights the target. Sidebar steps (nav-*): anchored to
    // the right of the item so the popover lands in the main column instead
    // of overlapping the sidebar.
    position: s.selector.startsWith("[data-tour='nav-") ? "right" as const : "center" as const,
    content:
    // pr-7 reserves space for the reactour close (×) button in the
    // top-right corner so the step counter doesn't sit underneath it.
    <div className="space-y-2.5 pr-7">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
            {locale === "fr" ? "Visite guidée" : "Tour"}
          </span>
          <span className="text-[10px] font-medium text-muted-foreground/70">
            {i + 1} / {total}
          </span>
        </div>
        <p className="text-base font-semibold leading-tight text-foreground">
          {s.copy.title[locale]}
        </p>
        <p className="text-[13px] leading-relaxed text-muted-foreground">
          {s.copy.body[locale]}
        </p>
      </div>
  }));
}

/**
 * Inner component — uses the TourProvider context to drive auto-start.
 * Must live inside <TourProvider> so `useTour()` resolves.
 */
function TourAutoStart() {
  const $ = _c(7);
  const {
    accessToken,
    user
  } = useAuthStore();
  const {
    setIsOpen,
    setCurrentStep
  } = useTour();
  const pathname = usePathname();
  const tosAccepted = Boolean(user?.tos_accepted_at);
  let t0;
  let t1;
  if ($[0] !== accessToken || $[1] !== pathname || $[2] !== setCurrentStep || $[3] !== setIsOpen || $[4] !== tosAccepted) {
    t0 = () => {
      if (!accessToken || !tosAccepted) {
        return;
      }
      if (!pathname || !/\/(fr|en)\/dashboard$/.test(pathname)) {
        return;
      }
      const alreadyRan = _temp;
      const startTour = delayMs => {
        if (alreadyRan()) {
          return;
        }
        setTimeout(() => {
          setCurrentStep(0);
          setIsOpen(true);
        }, delayMs);
      };
      let cancelled = false;
      (async () => {
        ;
        try {
          const data = await usersApi.onboarding(accessToken);
          if (cancelled) {
            return;
          }
          if (data.welcome_seen) {
            startTour(400);
          }
        } catch (t2) {
          const err = t2;
          console.error("[DashboardTour] check failed:", err);
        }
      })();
      const onWelcomeClosed = () => startTour(300);
      window.addEventListener("velyq:welcome-closed", onWelcomeClosed);
      return () => {
        cancelled = true;
        window.removeEventListener("velyq:welcome-closed", onWelcomeClosed);
      };
    };
    t1 = [accessToken, tosAccepted, pathname, setIsOpen, setCurrentStep];
    $[0] = accessToken;
    $[1] = pathname;
    $[2] = setCurrentStep;
    $[3] = setIsOpen;
    $[4] = tosAccepted;
    $[5] = t0;
    $[6] = t1;
  } else {
    t0 = $[5];
    t1 = $[6];
  }
  useEffect(t0, t1);
  return null;
}
function _temp() {
  return typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY) === "1";
}
interface DashboardTourProps {
  locale: string;
  children: React.ReactNode;
}
export function DashboardTour(t0) {
  const $ = _c(14);
  const {
    locale,
    children
  } = t0;
  const lang = locale === "en" ? "en" : "fr";
  let t1;
  if ($[0] !== lang) {
    t1 = buildSteps(lang);
    $[0] = lang;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const steps = t1;
  const handleClose = _temp2;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = t3 => {
      const {
        setIsOpen
      } = t3;
      handleClose();
      setIsOpen(false);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      popover: _temp5,
      maskArea: _temp6,
      maskWrapper: _temp7,
      badge: _temp8,
      controls: _temp9,
      close: _temp10
    };
    t4 = {
      mask: 10,
      popover: 16
    };
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  let t6;
  if ($[5] !== lang) {
    t5 = t7 => {
      const {
        currentStep,
        setCurrentStep
      } = t7;
      return currentStep === 0 ? null : <button type="button" onClick={() => setCurrentStep(currentStep - 1)} className="text-[12px] font-medium text-muted-foreground hover:text-foreground">{lang === "fr" ? "\u2190 Retour" : "\u2190 Back"}</button>;
    };
    t6 = t8 => {
      const {
        currentStep: currentStep_0,
        setCurrentStep: setCurrentStep_0,
        stepsLength,
        setIsOpen: setIsOpen_1
      } = t8;
      const isLast = currentStep_0 === stepsLength - 1;
      return <button type="button" onClick={() => {
        if (isLast) {
          handleClose();
          setIsOpen_1(false);
        } else {
          setCurrentStep_0(currentStep_0 + 1);
        }
      }} className="rounded-md bg-primary px-3 py-1.5 text-[12px] font-medium text-primary-foreground hover:opacity-90">{isLast ? lang === "fr" ? "Terminer" : "Finish" : lang === "fr" ? "Suivant \u2192" : "Next \u2192"}</button>;
    };
    $[5] = lang;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <TourAutoStart />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== children || $[10] !== steps || $[11] !== t5 || $[12] !== t6) {
    t8 = <TourProvider steps={steps} onClickClose={t2} onClickMask={_temp3} afterOpen={_temp4} beforeClose={handleClose} styles={t3} padding={t4} disableInteraction={true} showBadge={false} prevButton={t5} nextButton={t6}>{t7}{children}</TourProvider>;
    $[9] = children;
    $[10] = steps;
    $[11] = t5;
    $[12] = t6;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}
function _temp10(base_4) {
  return {
    ...base_4,
    color: "var(--muted-foreground)"
  };
}
function _temp9(base_3) {
  return {
    ...base_3,
    marginTop: 18
  };
}
function _temp8(base_2) {
  return {
    ...base_2,
    backgroundColor: "var(--primary)",
    color: "var(--primary-foreground)"
  };
}
function _temp7(base_1) {
  return {
    ...base_1,
    color: "rgba(0, 0, 0, 0.7)"
  };
}
function _temp6(base_0) {
  return {
    ...base_0,
    rx: 12
  };
}
function _temp5(base) {
  return {
    ...base,
    backgroundColor: "var(--popover)",
    color: "var(--popover-foreground)",
    borderRadius: "16px",
    border: "1px solid var(--border)",
    boxShadow: "0 24px 64px -16px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)",
    padding: "22px 24px",
    maxWidth: "440px",
    minWidth: "360px"
  };
}
function _temp4() {}
function _temp3(t0) {}
function _temp2() {
  if (typeof window !== "undefined") {
    localStorage.setItem(STORAGE_KEY, "1");
  }
}